import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import { Link } from 'react-router-dom';
import { getDashboardData } from '../../services/ApiService';
import LoadingSpinner from '../../components/client/LoadingSpinner';
import moment from "moment";
import DatePickerField from '../../partials/DatePickerField';
import AnalyticsDetailsModal from '../../components/superadmin/AnalyticsDetailsModal';

// Utility function to format date as dd-MM-yyyy
const formatDate = (date) => {
    if (!date) return null;
    return moment(date).format('DD-MM-YYYY');
};

// Helper function to initialize charts
const initializeChart = (ref, type, data, options) => {
    if (ref.current) {
        new Chart(ref.current, {
            type,
            data,
            options,
        });
    }
};

const SuperAdminHomeDev = () => {
    const [data, setData] = useState();
    const [dataLoading, setDataLoading] = useState(true);
    const [selectedInterval, setSelectedInterval] = useState('All Time');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [modalShow, setModalShow] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [modalTitle, setModalTitle] = useState('');

    // Chart refs
    const userStatsChartRef = useRef(null);
    const organicSocialChartRef = useRef(null);
    const paidSocialChartRef = useRef(null);
    const paidMediaChartRef = useRef(null);

    const fetchAdminDashboardDetails = async () => {
        try {
            setDataLoading(true);
            const result = await getDashboardData(selectedInterval, fromDate, toDate);
            if (result?.success) {
                setData(result?.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setDataLoading(false);
        }
    };

    useEffect(() => {
        fetchAdminDashboardDetails();
    }, [selectedInterval, fromDate, toDate]);

    // Initialize Charts
    useEffect(() => {
        // Active Users Chart
        initializeChart(userStatsChartRef, 'line', {
            labels: ['Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
            datasets: [{
                label: 'Active Users',
                data: [200, 200, 250, 300, 411],
                borderColor: '#7f56d9',
                fill: false,
            }]
        }, { plugins: { legend: { display: false } } });

        // Social Media Charts
        const socialChartOptions = {
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                    align: 'center',
                    labels: {
                        boxWidth: 12,
                        padding: 10,
                        font: { size: 10 },
                    },
                },
                tooltip: {
                    enabled: true,
                    callbacks: {
                        label: (tooltipItem) => {
                            const currentValue = tooltipItem.dataset.data[tooltipItem.dataIndex];
                            return `${tooltipItem.label}: ${currentValue}%`;
                        },
                    },
                },
            },
            responsive: true,
            maintainAspectRatio: false,
        };

        // Organic Social Chart
        initializeChart(organicSocialChartRef, 'doughnut', {
            labels: ['Facebook', 'Instagram', 'Tiktok', 'Linkedin', 'Twitter', 'Snapchat'],
            datasets: [{
                data: [10, 20, 30, 10, 20, 10],
                backgroundColor: ['#b4b1cb', '#d3a4ff', '#ffe58a', '#ffb1e0', '#33FFF2', '#95f3fa'],
            }]
        }, socialChartOptions);

        // Paid Social Chart
        initializeChart(paidSocialChartRef, 'doughnut', {
            labels: ['Facebook', 'Instagram', 'Tiktok', 'Linkedin', 'Twitter', 'Snapchat'],
            datasets: [{
                data: [10, 30, 20, 20, 10, 10],
                backgroundColor: ['#b4b1cb', '#d3a4ff', '#ffe58a', '#ffb1e0', '#33FFF2', '#95f3fa'],
            }]
        }, socialChartOptions);

        // Paid Media Chart
        initializeChart(paidMediaChartRef, 'doughnut', {
            labels: ['Google Ads', 'Bing Ads'],
            datasets: [{
                data: [40, 20],
                backgroundColor: ['#33FFF2', '#d3a4ff'],
            }]
        }, socialChartOptions);
    }, [data]);

    const summaryData = [
        { backgroundColor: '#7f56d9', icon: 'bi bi-currency-dollar', title: 'Total Spent', value: data?.total_spent || 0, type: 'spending' },
        { backgroundColor: '#008ffb', icon: 'bi bi-currency-dollar', title: 'Total Earnings', value: data?.total_subscription_value || 0, type: 'earnings' },
        { backgroundColor: '#3f0b74', icon: 'bi bi-person-fill', title: 'Total Clients', value: data?.total_clients || 0, type: 'clients' },
        { backgroundColor: '#FFB200', icon: 'bi bi-person-lines-fill', title: 'Total Users', value: data?.total_users || 0, type: 'users' },
        { backgroundColor: '#81c14b', icon: 'bi bi-bar-chart-line', title: 'Total Programs', value: data?.total_programs || 0, type: 'programs' },
        { backgroundColor: '#0b4f74', icon: 'bi bi-graph-up', title: 'Total Campaigns', value: data?.total_external_campaigns || 0, type: 'campaigns' },
        { backgroundColor: '#cd8200', icon: 'bi bi-plug', title: 'Total Integrations', value: data?.total_integrations || 0, type: 'integrations' },
        { backgroundColor: '#eb5454', icon: 'bi bi-bar-chart', title: 'Total Impressions', value: data?.total_impressions || 0, type: 'impressions' },
    ];

    const handleCardClick = (cardData) => {
        setModalType(cardData.type);
        setModalTitle(cardData.title);
        setModalShow(true);
    };

    // Handle interval selection
    const handleIntervalClick = (interval) => {
        setFromDate(null);
        setToDate(null); // Clear date range when an interval is selected
        setSelectedInterval(interval); // Set the selected interval
    };

    // Handle date selection
    const handleDateChange = (type, date) => {
        setSelectedInterval(null); // Reset interval if dates are selected
        const formattedDate = formatDate(date);

        if (type === 'from') {
            setFromDate(formattedDate);
        } else {
            setToDate(formattedDate);
        }
    };

    return (
        <div className="container-fluid d-flex flex-column gap_lg p-0">
            <div className="row g-3">
                {/* Datepickers Section */}
                <div className="col-xxl-5 d-flex gap_lg">
                    <DatePickerField label="From Date" 
                    selected={fromDate} 
                    onChange={(date) => handleDateChange('from', date)} 
                    placeholder="From Date"
                    
                    />
                    <DatePickerField label="To Date" selected={toDate} onChange={(date) => handleDateChange('to', date)} 
                    placeholder="To Date" />
                </div>

                {/* Buttons Section */}
                <div className="col-xxl-7 d-flex flex-wrap justify-content-xxl-between gap_md align-items-xxl-end">
                    {[{ label: 'This Week', value: 'week' },
                      { label: 'This Month', value: 'month' },
                      { label: '3 Months', value: '3_months' },
                      { label: '6 Months', value: '6_months' },
                      { label: 'This Year', value: 'year' },
                      { label: 'All Time', value: 'All Time' }
                    ].map((interval, index) => (
                        <IntervalButton
                            key={index}
                            label={interval.label}
                            onClick={() => handleIntervalClick(interval.value)}
                            isSelected={selectedInterval === interval.value}
                        />
                    ))}
                </div>
            </div>

            {dataLoading ? <LoadingSpinner /> : (
                <>
                    <div className="row g-4">
                        {summaryData.map((data, index) => (
                            <SummaryCard
                                key={index}
                                backgroundColor={data.backgroundColor}
                                icon={data.icon}
                                title={data.title}
                                value={data.value}
                                onClick={() => handleCardClick(data)}
                            />
                        ))}
                    </div>

                    <AnalyticsDetailsModal
                        show={modalShow}
                        setShow={setModalShow}
                        title={modalTitle}
                        type={modalType}
                        interval={selectedInterval === 'All Time' ? null : selectedInterval}
                        fromDate={fromDate}
                        toDate={toDate}
                    />

                    {/* Recent Activities & Charts */}
                    <div className="row g-4">
                        <div className="col-12 col-xl-5">
                            <div className="custom-card">
                                <div className="custom-card-header">Recent Activities</div>
                                <div className="custom-card-body">
                                    <ul className="p-0">
                                        {['Stanford University', 'Yale University', 'MIT'].map((item, index) => (
                                            <li key={index} className="d-flex gap-4 py-4 border_bottom">
                                                <span>{index + 1}</span>
                                                <Link to="#">{item}</Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-xl-7">
                            <div className="custom-card">
                                <div className="custom-card-header">Active Users Overview</div>
                                <div className="custom-card-body">
                                    <canvas ref={userStatsChartRef} style={{ height: '250px' }}></canvas>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Other Charts */}
                    <div className="row g-4">
                        {[{ ref: organicSocialChartRef, title: 'Organic Social' },
                            { ref: paidSocialChartRef, title: 'Paid Social' },
                            { ref: paidMediaChartRef, title: 'Paid Media' }
                        ].map((chart, index) => (
                            <div key={index} className="col-xl-4 col-md-6 col-12">
                                <div className="custom-card">
                                    <div className="custom-card-header">{chart.title}</div>
                                    <div className="custom-card-body d-flex justify-content-center align-items-center" style={{ height: '250px' }}>
                                        <canvas ref={chart.ref}></canvas>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

// Summary Card Component
const SummaryCard = ({ backgroundColor, icon, title, value, onClick }) => (
    <div className="col-xxl-3 col-lg-4 col-md-6 col-sm-12">
        <div 
            className="summary-box text-white p-4 custom-card" 
            style={{ backgroundColor, cursor: 'pointer' }}
            onClick={onClick}
        >
            <div className="d-flex gap-3">
                <div className="border_radius_sm" style={{ width: '60px', height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.15)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <i className={icon} style={{ fontSize: '40px', color: 'white' }}></i>
                </div>
                <div>
                    <h6>{title}</h6>
                    <h5>{value}</h5>
                </div>
            </div>
        </div>
    </div>
);

// Interval Button Component
const IntervalButton = ({ label, onClick, isSelected }) => (
    <button
        className={`btn btn-md py-2 px-3 border rounded-pill ${isSelected ? 'btn-primary' : ''}`}
        onClick={onClick}
    >
        <span className="small">{label}</span>
    </button>
);

export default SuperAdminHomeDev;
